/**
 * MyFNG Complaint Service for Web App
 * Temporary service for registering MyFNG complaints via external API
 */

import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../config/env';

const supabaseRequest = async (endpoint, options = {}) => {
  const response = await fetch(`${SUPABASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  return response.json();
};

const MYFNG_API_URL = 'https://022os10kr2.execute-api.ap-south-1.amazonaws.com/enterprise/66f6bc6faf29b5a6f29c9bbf/autoupdatelead';
const MYFNG_BEARER_TOKEN = '398fc0c7-ee90-4992-b214-4063f9f7ad031727771960659:e9580bb4-cb6f-47ff-81fb-847e5a98a5a2';

export class MyFNGComplaintService {
  /**
   * Register a MyFNG complaint via external API
   */
  static async registerComplaint(complaintData, registeredBy) {
    try {
      // Prepare API request body
      const requestBody = {
        fields: {
          Name: complaintData.chat_name,
          Phone: complaintData.waba,
          LEADTAG: 'DL-Service',
          carModel: complaintData.car_model,
          REMARK: complaintData.cust_keyword
        },
        actions: [
          {
            type: 'SYSTEM_NOTE',
            text: 'Lead Source: delhi_service'
          }
        ]
      };

      // Call external API
      const response = await fetch(MYFNG_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${MYFNG_BEARER_TOKEN}`
        },
        body: JSON.stringify(requestBody)
      });

      // Get response as text first - handle both text and potential errors
      let responseText = '';
      let responseData = null;

      try {
        responseText = await response.text();
        console.log('MyFNG API Response Text:', responseText);
        
        // Try to parse as JSON, if fails treat as plain text response
        if (responseText.trim().startsWith('{') || responseText.trim().startsWith('[')) {
          try {
            responseData = JSON.parse(responseText);
          } catch (parseError) {
            // Not valid JSON, treat as plain text
            responseData = {
              message: responseText,
              rawResponse: responseText
            };
          }
        } else {
          // Plain text response (like "OK", "Success", etc.)
          responseData = {
            message: responseText,
            rawResponse: responseText
          };
        }
      } catch (textError) {
        // If we can't even get text, create error response
        console.error('Error reading response:', textError);
        responseData = {
          message: 'Failed to read API response',
          rawResponse: textError.message || 'Unknown error'
        };
      }

      // Store in database if API call successful
      if (response.ok) {
        await this.saveToDatabase(complaintData, registeredBy, responseData, 'success');
        return {
          success: true,
          message: 'Complaint registered successfully',
          data: responseData
        };
      } else {
        // Store failed attempt in database
        const errorMsg = responseData?.message || responseData?.rawResponse || `API request failed with status ${response.status}`;
        await this.saveToDatabase(
          complaintData,
          registeredBy,
          responseData,
          'failed',
          errorMsg
        );
        return {
          success: false,
          error: errorMsg,
          data: responseData
        };
      }
    } catch (error) {
      // Handle network errors or other exceptions
      console.error('MyFNG API Error:', error);
      const errorMessage = error.message || 'Unknown error occurred';
      
      // Store error in database
      await this.saveToDatabase(
        complaintData,
        registeredBy,
        { error: errorMessage },
        'failed',
        errorMessage
      );
      
      // Return user-friendly error message
      let userMessage = 'Failed to register complaint';
      if (errorMessage.includes('JSON Parse')) {
        userMessage = 'API response format error. Please try again.';
      } else if (errorMessage.includes('Network')) {
        userMessage = 'Network error. Please check your connection.';
      }
      
      return {
        success: false,
        error: userMessage
      };
    }
  }

  /**
   * Save complaint registration to database
   */
  static async saveToDatabase(complaintData, registeredBy, apiResponse, status, errorMessage) {
    try {
      await supabaseRequest(`/rest/v1/myfng_complaints`, {
        method: 'POST',
        body: JSON.stringify({
          registered_by: registeredBy,
          chat_name: complaintData.chat_name,
          waba: complaintData.waba,
          car_model: complaintData.car_model,
          cust_keyword: complaintData.cust_keyword,
          lead_tag: 'DL-Service',
          lead_source: 'delhi_service',
          api_response: apiResponse,
          api_status: status,
          error_message: errorMessage || null
        }),
      });
      return { success: true };
    } catch (error) {
      console.error('Error saving to database:', error);
      // Don't throw error, just log it
      return { success: false, error: error.message };
    }
  }

  /**
   * Get all MyFNG complaints registered by a user
   */
  static async getComplaintsByUser(userId) {
    try {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/myfng_complaints?registered_by=eq.${userId}&order=created_at.desc`,
        {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch complaints');
      }
      
      const data = await response.json();
      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching complaints:', error);
      return { success: false, data: [], error: error.message };
    }
  }
}

